import axios from "axios";
import { getAuth } from "firebase/auth";
import { loadRecaptchaEnterpriseSimple, getRecaptchaToken } from "./recaptcha";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
if (!API_BASE_URL && typeof window !== "undefined") {
  console.warn("REACT_APP_API_BASE_URL is not set — API requests will use relative URLs");
}

/**
 * Fetch account types from the API
 * @returns {Promise<Array>} Array of account types with their allowed event types
 */
export const fetchAccountTypes = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/account-types/`, {
      timeout: 10000, // 10 second timeout - fail fast instead of hanging
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching account types:", error);
    throw error;
  }
};

export const getAuthHeader = async () => {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const headers = {
    "Content-Type": "application/json",
  };

  if (currentUser) {
    const token = await currentUser.getIdToken(); // Use cached token to avoid clock skew issues
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

/** ZIP from the saved address row — used when context omits zipcode (e.g. older API). */
export async function fetchUserAddressZipcode(headers) {
  try {
    const authHeaders = headers || (await getAuthHeader());
    const response = await axios.get(
      `${API_BASE_URL}/api/users/me/address/`,
      { headers: authHeaders }
    );
    const zip = (response.data?.zipcode || "").trim();
    return zip || null;
  } catch {
    return null;
  }
}

export const fetchUserContext = async () => {
  try {
    const headers = await getAuthHeader();
    const response = await axios.get(
      `${API_BASE_URL}/api/user/context/`,
      { headers }
    );
    const userData = response.data || {};
    let zipcode = userData.zipcode ?? null;
    if (!zipcode) {
      zipcode = await fetchUserAddressZipcode(headers);
    }
    return {
      userDnaId: userData.id ?? null,
      userRepsName: userData.representatives_name ?? "",
      userSenOneName: userData.senator_one_name ?? "",
      userSenTwoName: userData.senator_two_name ?? "",
      userSenOneBioguideID: userData.senator_one_bioguide_id ?? "",
      userSenTwoBioguideID: userData.senator_two_bioguide_id ?? "",
      bioguideId: userData.representative_bioguide_id ?? "",
      verifiedUser: userData.is_verified ?? false,
      userDistrict: userData.district_number ?? "",
      userState: userData.state ?? "",
      userFirstName: userData.first_name ?? "",
      userLastName: userData.last_name ?? "",
      accountTypeName: userData.account_type_name ?? null,
      hasSelectedAccountType: userData.has_selected_account_type ?? false,
      username: userData.username ?? null,
      isProfilePublic: userData.is_profile_public ?? false,
      requiresUsername: userData.requires_username ?? false,
      canCreateEvent: userData.can_create_event ?? false,
      profileImage: userData.profile_image ?? null,
      entityName: userData.entity_name ?? null,
      municipalityName: userData.municipality_name ?? null,
      municipalityDepartment: userData.municipality_department ?? null,
      isStripeVerified: userData.is_stripe_verified ?? false,
      politicianVerificationStatus: userData.politician_verification_status ?? null,
      verificationRejectionReason: userData.verification_rejection_reason ?? null,
      citizenManualVerificationStatus: userData.citizen_manual_verification_status ?? null,
      hasAcceptedCommunityGuidelines: userData.has_accepted_community_guidelines ?? false,
      publicOfficeStatus: userData.public_office_status ?? null,
      publicOfficeLevel: userData.public_office_level ?? null,
      publicOfficePosition: userData.public_office_position ?? null,
      zipcode,
      entitlements: userData.entitlements ?? null,
    };
  } catch (error) {
    console.error("Error fetching user context:", error.response?.data || error.message);
    return null;
  }
};


// Function to fetch comments for a bill
export const fetchComments = async (billId, { verifiedOnly = false } = {}) => {
  try {
    const params = new URLSearchParams({ bill_id: billId });
    if (verifiedOnly) params.set("verified_only", "true");
    // Headers are optional here, but sending them lets the API keep your own
    // pending comments visible and return your current vote.
    const headers = await getAuthHeader();
    const response = await axios.get(
      `${API_BASE_URL}/api/get_comments/?${params.toString()}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching comments:", error);
    throw error;
  }
};

// Function to post a new comment
export const postComment = async (billId, content, vote, anonymous) => {
  const headers = await getAuthHeader(); 

  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/comments/`,
      {
        bill: billId,
        content,
        vote,
        anonymous,
      },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error posting comment:", error);
    throw error;
  }
};

// Function to upvote or downvote a comment 
export const commentVote = async (commentId, vote) => {
  try {
    const headers = await getAuthHeader();
   
    const response = await axios.post(
      `${API_BASE_URL}/api/comments/vote/`,
      {
        comment_id: commentId,
        vote: vote,
      },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error voting on comment:", error);
    throw error;
  }
};

export const postReply = async (commentId, reply, anonymous = true) => {
  try {
    const headers = await getAuthHeader();
   
    const response = await fetch(`${API_BASE_URL}/api/comments/${commentId}/replies/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ content: reply, anonymous }), 
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Failed to post reply: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};
 
export const fetchReplies = async (commentId, { verifiedOnly = false } = {}) => {
  const params = verifiedOnly ? "?verified_only=true" : "";
  const headers = await getAuthHeader();
  const response = await fetch(
    `${API_BASE_URL}/api/comments/${commentId}/replies/${params}`,
    { headers }
  );

  if (!response.ok) {
    throw new Error("Failed to load replies");
  }

  return await response.json();
};

export const deleteReply = async (replyId, commentId) => {
  const headers = await getAuthHeader();

  const res = await fetch(
    `${API_BASE_URL}/api/comments/${commentId}/replies/${replyId}/`,
    { method: 'DELETE', headers }
  );

  if (res.status === 204) return;  
 
  let detail = `Failed to delete reply (status ${res.status})`;
  try {
    const data = await res.json();
    if (data?.detail) detail = data.detail;
  } catch {}
  throw new Error(detail);
};

export const editReply = async (replyId, commentId, content) => {
  const headers = await getAuthHeader();

  const res = await fetch(
    `${API_BASE_URL}/api/comments/${commentId}/replies/${replyId}/`,
    {
      method: "PUT",
      headers,
      body: JSON.stringify({ content }),
    }
  );

  if (res.ok) return await res.json();

  let detail = `Failed to edit reply (status ${res.status})`;
  try {
    const data = await res.json();
    if (data?.detail) detail = data.detail;
  } catch {}
  throw new Error(detail);
};

 
/**
 * Fetch distinct policy areas for bills filter.
 * @returns {Promise<string[]>}
 */
export async function fetchPolicyAreas() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/policy-areas/`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.policy_areas || [];
  } catch {
    return [];
  }
}

/**
 * Fetch bills - aligned with mobile api.js fetchBills.
 * Params: sponsorIds/cosponsorIds arrays (any members) OR legacy activeFilters + repId/senOneId/senTwoId.
 */
export async function fetchBills(
  page = 1,
  pageSize = 15,
  searchQuery = "",
  activeFilters = [],
  repId = "",
  senOneId = "",
  senTwoId = "",
  filterModel = {},
  orderBy = "-last_action_date",
  sponsorIdsParam = [],
  cosponsorIdsParam = [],
  hasActiveCampaign = false,
  verifiedVotesOnly = false
) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  const q = (searchQuery || "").trim();
  if (q) params.append("search", q);
  if (orderBy) params.append("order_by", orderBy);

  const sponsorIds = new Set(sponsorIdsParam || []);
  const cosponsorIds = new Set(cosponsorIdsParam || []);

  if (sponsorIds.size === 0 && cosponsorIds.size === 0) {
    activeFilters.forEach((filter) => {
      switch (filter) {
        case "sponsored_rep":
          if (repId) sponsorIds.add(repId);
          break;
        case "cosponsored_rep":
          if (repId) cosponsorIds.add(repId);
          break;
        case "sponsored_sen_one":
          if (senOneId) sponsorIds.add(senOneId);
          break;
        case "cosponsored_sen_one":
          if (senOneId) cosponsorIds.add(senOneId);
          break;
        case "sponsored_sen_two":
          if (senTwoId) sponsorIds.add(senTwoId);
          break;
        case "cosponsored_sen_two":
          if (senTwoId) cosponsorIds.add(senTwoId);
          break;
        default:
          break;
      }
    });
  }

  sponsorIds.forEach((id) => params.append("sponsor_id", id));
  cosponsorIds.forEach((id) => params.append("cosponsor_id", id));

  if (filterModel?.items?.length) {
    params.append("filters", JSON.stringify(filterModel.items));
  }

  if (hasActiveCampaign) {
    params.append("has_active_campaign", "true");
  }

  if (verifiedVotesOnly) {
    params.append("verified_only", "true");
  }

  const response = await fetch(`${API_BASE_URL}/api/bills/?${params.toString()}`, {
    headers: await getAuthHeader(),
  });
  if (!response.ok) {
    throw new Error(`Network error (${response.statusText})`);
  }
  return response.json();
}


export async function fetchBillDetail(bill_id, { verifiedOnly = false } = {}) {
  try {
    const qs = verifiedOnly ? '?verified_only=true' : '';
    const response = await fetch(`${API_BASE_URL}/api/bills/${bill_id}/${qs}`, {
      headers: await getAuthHeader(),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch bill ${bill_id}: ${response.statusText}`);
    }
    const data = await response.json();
    return data.bill;
  } catch (error) {
    throw error;
  }
}

export async function fetchBillBySlug(slug, { verifiedOnly = false } = {}) {
  const qs = verifiedOnly ? '?verified_only=true' : '';
  const response = await fetch(
    `${API_BASE_URL}/api/bills/by-slug/${encodeURIComponent(slug)}/${qs}`,
    { headers: await getAuthHeader() },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch bill ${slug}: ${response.statusText}`);
  }
  const data = await response.json();
  return data.bill;
}

export function buildBillSlug(bill) {
  if (bill?.type && bill?.bill_number) {
    return `${String(bill.type).toLowerCase()}-${String(bill.bill_number)}`;
  }
  return String(bill?.id || '');
}

export const updateBillVote = async (billId, vote) => {
  const headers = await getAuthHeader(); // must include Firebase token

  const response = await fetch(`${API_BASE_URL}/api/bill_votes/`, {
    method: "POST",
    headers,
    body: JSON.stringify({ bill_id: billId, vote }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to update vote.");
  }

  return await response.json();
};


export const getBillVote = async (billId, { verifiedOnly = false } = {}) => {
  try {
    const headers = await getAuthHeader();

    const url = new URL(`${API_BASE_URL}/api/bill_votes/`);
    url.searchParams.append("bill_id", billId);
    if (verifiedOnly) url.searchParams.append("verified_only", "true");

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `Failed to get vote: ${response.statusText}`);
    }

    const data = await response.json();

    // Expecting: { vote: true/false/null, like_count: int, dislike_count: int }
    return data;
  } catch (error) {
    console.error("Error fetching bill vote:", error);
    throw error;
  }
};


export const fetchAllElections = async (
  page = 1,
  pageSize = 10,
  search = '',
  filters = []
) => {
  try {
    const url = new URL(`${API_BASE_URL}/api/elections/`);
    url.searchParams.append('page', page);
    url.searchParams.append('page_size', pageSize);

    if (search) {
      url.searchParams.append('search', search);
    }

    if (filters.length > 0) {
      url.searchParams.append('filters', JSON.stringify(filters));
    }

    const response = await fetch(url);
    const data = await response.json();

    if (data?.elections) {
      return {
        success: true,
        data: data.elections,
        totalCount: data.totalCount,
      };
    } else {
      return { success: false, error: "No election data found." };
    }
  } catch (err) {
    return { success: false, error: "Failed to fetch election data." };
  }
};

export const fetchElectionById = async (id) => {
  try {
    const url = new URL(`${API_BASE_URL}/api/elections/${id}/`);
    const response = await fetch(url);
    const data = await response.json();

    if (response.ok) {
      return { success: true, data };
    } else {
      return { success: false, error: data.detail || "Election not found." };
    }
  } catch (err) {
    return { success: false, error: "Failed to fetch election detail." };
  }
};

export const fetchProducts = async () => {
  try {
    const headers = await getAuthHeader();

    const response = await fetch(`${API_BASE_URL}/api/products/`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "Failed to fetch products");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
};

export const sendContactForm = async (formData) => {
  try {
    // Checkbox must already be rendered on the page; token from completed widget
    await loadRecaptchaEnterpriseSimple();
    const recaptcha_token = getRecaptchaToken();
    
    const response = await fetch(`${API_BASE_URL}/api/contact/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...formData,
        recaptcha_token,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to send form data.");
    }

    return { success: true };
  } catch (err) {
    console.error("Contact form error:", err);
    return { success: false, error: err.message };
  }
};

  
export async function calculateRepresentative(formData) {
  
  const headers = await getAuthHeader();

  // Look up the district + rep by address
  const districtRes = await axios.post(
    `${API_BASE_URL}/api/representative_by_address/`,
    {
      street_1: formData.street_1,
      city:     formData.city,
      state:    formData.state,
      zip_code: formData.zipcode,
    },
    { headers }
  );

  const { district, representative } = districtRes.data;

  // 3️ Persist this back to the user’s profile
  await axios.put(
    `${API_BASE_URL}/api/users/me/user_address/`,
    {
      district_number:             district,
      representatives_name:        representative?.name        || null,
      representative_bioguide_id:  representative?.bioguideId  || null,
      street_1:                    formData.street_1,
      city:                        formData.city,
      state:                       formData.state,
      zipcode:                     formData.zipcode,
      has_met_representative:      formData.hasMetRep,
      first_name:                 formData.first_name,
      last_name:                  formData.last_name,
    },
    { headers }
  );

  // 4️ Return the rep object for UI use
  return representative;
}

export async function fetchUserFullProfileData(setFormData, setError, setLoading) {
  try {
    setLoading(true);
    const headers = await getAuthHeader();
    const response = await axios.get(`${API_BASE_URL}/api/users/me/full_profile/`, { headers });
    const user = response.data.user || {};
    const address = response.data.address || {};

    setFormData({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      entity_name: user.entity_name || "",
      email: user.email || "",
      username: user.username || "",
      phone_number: user.phone_number || "",
      street_1: address.street_1 || "",
      city: address.city || "",
      state: address.state || "",
      zipcode: address.zipcode || "",
      pronouns: user.pronouns || "",
      bio: user.bio || "",
      representatives_name: user.representatives_name || "",
      senator_one_name: user.senator_one_name || "",
      senator_two_name: user.senator_two_name || "",
      account_type: user.account_type || null,
      account_type_name: user.account_type?.name ?? null,
      is_state_visible: user.is_state_visible ?? true,
      is_district_visible: user.is_district_visible ?? true,
      is_profile_public: user.is_profile_public ?? false,
      occupation: user.occupation ?? null,
      occupation_other: user.occupation_other || "",
      home_status: user.home_status ?? null,
      gender_identity: user.gender_identity ?? null,
      gender_identity_other: user.gender_identity_other || "",
      age_range: user.age_range ?? null,
      veteran_military_status: user.veteran_military_status ?? null,
      parent_caregiver_status: user.parent_caregiver_status ?? null,
      relationship_status: user.relationship_status ?? null,
      show_relationship_status: user.show_relationship_status ?? false,
      political_party: user.political_party ?? null,
      political_party_other: user.political_party_other || "",
      current_office: user.current_office || "",
      district_jurisdiction: user.district_jurisdiction || "",
      years_in_office: user.years_in_office ?? null,
      office_seeking: user.office_seeking || "",
      election_year: user.election_year ?? null,
      campaign_website: user.campaign_website || "",
      public_office_status: user.public_office_status || "",
      public_office_level: user.public_office_level || "",
      public_office_position: user.public_office_position || "",
      organization_subtype: user.organization_subtype ?? null,
      organization_type: user.organization_type ?? null,
      organization_type_other: user.organization_type_other || "",
      organization_size: user.organization_size ?? null,
      year_founded: user.year_founded ?? null,
      focus_areas: Array.isArray(user.focus_areas) ? user.focus_areas : [],
      organization_mission: user.organization_mission || "",
      organization_contact_email: user.organization_contact_email || "",
      organization_legal_name: user.organization_legal_name || "",
      twitter_url: user.twitter_url || "",
      instagram_url: user.instagram_url || "",
      facebook_url: user.facebook_url || "",
      website_url: user.website_url || "",
      social_links: Array.isArray(user.social_links) ? user.social_links : [],
      profile_image: user.profile_image || null,
    });
  } catch (error) {
    console.error("Error fetching user data:", error);
    if (setError) setError("Failed to load profile data.");
  } finally {
    if (setLoading) setLoading(false);
  }
}

export async function fetchUserAddress(setFormData, setError, setLoading ) {
  try {
    setLoading(true);
    const headers = await getAuthHeader();
    const response = await axios.get(`${API_BASE_URL}/api/users/me/address/`, { headers });
    const user = response.data;

    setFormData({ 
      street_1: user.street_1 || "",
      city: user.city || "",
      state: user.state || "",
      zipcode: user.zipcode || "", 
    });

  } catch (error) {
    console.error("Error fetching user address data:", error);
    setError("Failed to load address data.");
  } finally {
    setLoading(false);
  }
} 




export async function updateUserProfile(formData) {
  const headers = await getAuthHeader();

  const payload = {
    ...formData,
    state: typeof formData.state === "object" ? formData.state.value : formData.state,
    pronouns: typeof formData.pronouns === "object" ? formData.pronouns.value : formData.pronouns,
  };

  await axios.put(`${API_BASE_URL}/api/users/me/full_profile_update/`, payload, { headers });
}

export async function fetchUserRepresentativeName() {
  try {
    const headers = await getAuthHeader();
    const response = await axios.get(`${API_BASE_URL}/api/users/me/full_profile/`, { headers });

    const user = response.data.user || {};
    return {
      representatives_name: user.representatives_name || "",
      senator_one_name: user.senator_one_name || "",
      senator_two_name: user.senator_two_name || "",
    };
  } catch (error) {
    console.error("Error fetching representative name:", error);
    throw new Error("Failed to load representative name.");
  }
}

/** PUT /api/users/me/full_profile_update/ - partial or full profile payload */
export async function updateFullProfile(updates) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${API_BASE_URL}/api/users/me/full_profile_update/`,
    updates,
    { headers }
  );
  return response.data;
}

/** GET /api/account/deletion-preview/ - read-only summary before account deletion */
export async function fetchDeletionPreview() {
  const headers = await getAuthHeader();
  const response = await axios.get(`${API_BASE_URL}/api/account/deletion-preview/`, { headers });
  return response.data;
}

/** DELETE /api/account/delete/ - permanently delete account */
export async function deleteAccount({ appleAuthorizationCode } = {}) {
  const headers = await getAuthHeader();
  const body = appleAuthorizationCode ? { apple_authorization_code: appleAuthorizationCode } : undefined;
  const response = await axios.delete(`${API_BASE_URL}/api/account/delete/`, { headers, data: body });
  return response.data;
}

/** PUT /api/users/me/account-type/ - payload: { account_type_name } or { account_type_id } */
export async function updateUserAccountTypeWithData(payload) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${API_BASE_URL}/api/users/me/account-type/`,
    payload,
    { headers }
  );
  return response.data;
}

/** PUT /api/users/me/volunteer-preferences/ */
export async function updateVolunteerPreferences(preferences) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${API_BASE_URL}/api/users/me/volunteer-preferences/`,
    preferences,
    { headers }
  );
  return response.data;
}
 

export class LoginUserError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "LoginUserError";
    this.status = status;
  }
}

export const loginUser = async () => {
  try {
    const headers = await getAuthHeader();

    const res = await axios.get(`${API_BASE_URL}/api/users/login/`, {
      headers,
    });

    const { userId, bioguideId } = res.data;

    return { userId, bioguideId };
  } catch (error) {
    console.error("Error ensuring backend user:", error.response || error);
    const status = error.response?.status;
    const message =
      error.response?.data?.error ||
      error.response?.data?.detail ||
      error.message ||
      "User verification failed.";
    throw new LoginUserError(message, status);
  }
};

// Called on Signup.js. Passes confirmed_over_18 so the backend records the age confirmation.
export const createUnverifiedUser = async (
  { confirmed_over_18 = false, first_name = "", last_name = "" } = {},
  retry = true
) => {
  try {
    const headers = await getAuthHeader();

    if (!headers.Authorization) {
      throw new Error("Authentication token not available. Please try again.");
    }

    await axios.post(
      `${API_BASE_URL}/api/users/create_unverified/`,
      { confirmed_over_18, first_name, last_name },
      { headers }
    );
  } catch (error) {
    console.error("Error creating unverified user:", error.response?.status || error.message);

    const status = error.response?.status;
    const backendMessage =
      error.response?.data?.error ||
      error.response?.data?.detail ||
      error.message ||
      "Unverified user creation failed.";
    const isAuthError =
      status === 401 ||
      status === 403 ||
      String(backendMessage).toLowerCase().includes("token") ||
      String(backendMessage).toLowerCase().includes("auth");

    // A brand-new Firebase account can race ahead of token propagation; give it
    // one forced refresh before giving up.
    if (retry && isAuthError) {
      await new Promise((resolve) => setTimeout(resolve, 600));
      try {
        await getAuth().currentUser?.getIdToken(true);
      } catch (tokenError) {
        console.warn("Token refresh before create_unverified retry failed.");
      }
      return createUnverifiedUser({ confirmed_over_18, first_name, last_name }, false);
    }

    throw new Error(backendMessage);
  }
};

export const socialSignUp = async (confirmedAge = false, nameData = {}) => {
  try {
    const headers = await getAuthHeader();

    if (!headers.Authorization) {
      throw new Error("Authentication token not available. Please try again.");
    }

    const response = await axios.post(
      `${API_BASE_URL}/api/users/create_user_social_signup/`,
      {
        confirmed_over_18: confirmedAge,
        first_name: nameData.first_name || "",
        last_name: nameData.last_name || "",
      },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating social signup user:", error.response?.data || error);

    if (error.response?.status === 401) {
      throw new Error("Authentication failed. Please try signing in again.");
    } else if (error.response?.status === 400) {
      const detail = error.response?.data?.detail || error.response?.data?.error;
      throw new Error(detail || "Invalid request. Please try again.");
    } else if (error.response?.status === 409) {
      throw new Error("An account with this email already exists. Try logging in instead.");
    } else if (!error.response) {
      throw new Error("Network error. Please check your connection and try again.");
    }

    throw new Error(error.response?.data?.error || error.response?.data?.detail || "Failed to create account. Please try again.");
  }
};

export const ensureBackendUser = loginUser;

export const signupUpdateAccountTypeWithData = async (payload) => {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${API_BASE_URL}/api/users/signup/account-type/`,
    payload,
    { headers }
  );
  return response.data;
};


export const checkUsernameAvailability = async (username) => {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${API_BASE_URL}/api/users/check_username/`,
    { username },
    { headers }
  );
  return response.data;
};

export const setUsername = async (username) => {
  const headers = await getAuthHeader();
  const response = await axios.post(
    `${API_BASE_URL}/api/users/set_username/`,
    { username },
    { headers }
  );
  return response.data;
};


  
export async function calculateSenatorsAndRep(formData, claimedBioguideId = null) {
  
  const headers = await getAuthHeader();

  // Look up the district + rep by address
  const districtRes = await axios.post(
    `${API_BASE_URL}/api/get_senators_and_rep/`,
    {
      street_1: formData.street_1,
      city:     formData.city,
      state:    formData.state,
      zip_code: formData.zipcode,
    },
    { headers }
  );
  // console.log("District response:", districtRes.data);
  const { district, representative, senator_one, senator_two } = districtRes.data;
  // District 0 is valid (at-large states and DC), so don't treat it as missing.
  const districtStr = district === null || district === undefined ? null : String(district);
  // console.log("District:", district, "Representative:", representative, "Senator One:", senator_one, "Senator Two:", senator_two);
  // 3️ Persist this back to the user’s profile
  await axios.put(
    `${API_BASE_URL}/api/users/me/user_address/`,
    {
      district_number:              districtStr,
      representatives_name:         representative?.name        || null,
      representative_bioguide_id:   representative?.bioguideId  || null,
      senator_one_name:             senator_one?.name           || null,
      senator_one_bioguide_id:      senator_one?.bioguideId     || null,
      senator_two_name:             senator_two?.name           || null,
      senator_two_bioguide_id:      senator_two?.bioguideId     || null,
      street_1:                     formData.street_1,
      city:                         formData.city,
      state:                        formData.state,
      zipcode:                      formData.zipcode,
      has_met_representative:       formData.hasMetRep,
      has_met_senator_one:          formData.hasMetSenator1,
      has_met_senator_two:          formData.hasMetSenator2,
      first_name:                   formData.first_name,
      last_name:                    formData.last_name,
      entity_name:                  formData.entity_name || null,
      municipality_name:            formData.municipality_name || null,
      municipality_department:      formData.municipality_department || null,
    },
    { headers }
  );

  const profilePayload = {
    state: formData.state,
    district_number: districtStr,
  };
  if (claimedBioguideId) {
    profilePayload.claimed_bioguide_id = claimedBioguideId;
  }
  await axios.put(
    `${API_BASE_URL}/api/users/me/full_profile_update/`,
    profilePayload,
    { headers }
  );

  // 4️ Return the rep object for UI use
  return {
  district,
  representative,
  senator_one,
  senator_two,
};
}

/**
 * Resolve and persist reps from a state + district the user picked themselves.
 * Skips the Census Geocoder entirely, so it still works when Census is down and
 * for users who would rather not hand over a street address.
 */
export async function calculateSenatorsAndRepByDistrict(formData, claimedBioguideId = null) {
  const headers = await getAuthHeader();

  const districtRes = await axios.post(
    `${API_BASE_URL}/api/get_senators_and_rep_by_district/`,
    {
      state: formData.state,
      district: formData.district_number,
    },
    { headers }
  );

  const { district, representative, senator_one, senator_two } = districtRes.data;
  const districtStr = district === null || district === undefined ? null : String(district);

  // No street/city/zipcode is sent, so the backend leaves any existing Address row alone.
  await axios.put(
    `${API_BASE_URL}/api/users/me/user_address/`,
    {
      state:                        formData.state,
      district_number:              districtStr,
      representatives_name:         representative?.name        || null,
      representative_bioguide_id:   representative?.bioguideId  || null,
      senator_one_name:             senator_one?.name           || null,
      senator_one_bioguide_id:      senator_one?.bioguideId     || null,
      senator_two_name:             senator_two?.name           || null,
      senator_two_bioguide_id:      senator_two?.bioguideId     || null,
      has_met_representative:       formData.hasMetRep,
      has_met_senator_one:          formData.hasMetSenator1,
      has_met_senator_two:          formData.hasMetSenator2,
      first_name:                   formData.first_name,
      last_name:                    formData.last_name,
      entity_name:                  formData.entity_name || null,
      municipality_name:            formData.municipality_name || null,
      municipality_department:      formData.municipality_department || null,
    },
    { headers }
  );

  if (claimedBioguideId) {
    await axios.put(
      `${API_BASE_URL}/api/users/me/full_profile_update/`,
      { claimed_bioguide_id: claimedBioguideId },
      { headers }
    );
  }

  return { district, representative, senator_one, senator_two };
}

/**
 * Lightweight foreground save: persists just the four address fields via
 * PUT /api/users/me/user_address/ without recomputing reps. Use this when the
 * UI needs the address change to land quickly and the heavier rep recalculation
 * (calculateSenatorsAndRep) will run separately in the background.
 */
export async function saveAddressOnly({ street_1, city, state, zipcode }) {
  const headers = await getAuthHeader();
  const response = await axios.put(
    `${API_BASE_URL}/api/users/me/user_address/`,
    { street_1, city, state, zipcode },
    { headers }
  );
  return response.data;
}

/**
 * Persist address + rep/senator IDs + has_met flags without re-running district calculation.
 * Used in changeMode + confirmMode when the address hasn't changed.
 */
export async function saveUserAddressFlags(formData, repContext) {
  const headers = await getAuthHeader();
  await axios.put(
    `${API_BASE_URL}/api/users/me/user_address/`,
    {
      street_1:                     formData.street_1,
      city:                         formData.city,
      state:                        formData.state,
      zipcode:                      formData.zipcode,
      representatives_name:         repContext.repsName            || null,
      representative_bioguide_id:   repContext.repsBioguideID      || null,
      senator_one_name:             repContext.senOneName           || null,
      senator_one_bioguide_id:      repContext.senOneBioguideID    || null,
      senator_two_name:             repContext.senTwoName           || null,
      senator_two_bioguide_id:      repContext.senTwoBioguideID    || null,
      has_met_representative:       formData.hasMetRep,
      has_met_senator_one:          formData.hasMetSenator1,
      has_met_senator_two:          formData.hasMetSenator2,
      first_name:                   formData.first_name,
      last_name:                    formData.last_name,
      entity_name:                  formData.entity_name || null,
      municipality_name:            formData.municipality_name || null,
      municipality_department:      formData.municipality_department || null,
    },
    { headers }
  );
}


export const restoreUserSession = async (setUserId, setUserRepsBioguideID) => {
  try {
    const headers = await getAuthHeader();

    // If no auth token, exit early
    if (!headers.Authorization) return false;

    const res = await axios.get(`${API_BASE_URL}/api/users/login/`, { headers });

    const { userId, bioguideId } = res.data;

    if (setUserId) setUserId(userId);
    if (setUserRepsBioguideID) setUserRepsBioguideID(bioguideId);

    return true;
  } catch (error) {
    console.error("Failed to restore backend session:", error.response?.data || error.message);
    return false;
  }
};


export const generateAnalysis = async (billId) => {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/bills/${billId}/generate_analysis/`, {
    method: "POST",
    headers,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    if (response.status === 401) {
      throw new Error("Please log in to generate analysis.");
    }
    if (response.status === 429) {
      throw new Error("You can only generate one AI analysis per hour. Please try again later.");
    }
    throw new Error(data.detail || `Failed to generate analysis (${response.status})`);
  }
  return response.json();
};


export const getElectionsByState = async () => {
  try {
    const headers = await getAuthHeader();

    const response = await axios.get(`${API_BASE_URL}/api/elections_by_state/`, {
      headers,
    });

    // console.log("Elections by state response:", response.data);
    return response.data; // array of elections
  } catch (error) {
    console.error("Error fetching elections by state:", error.response?.data || error.message);
    return null;
  }
};

export const deleteComment = async (commentId) => {
  const headers = await getAuthHeader();

  const res = await fetch(`${API_BASE_URL}/api/comments/${commentId}/`, {
    method: "DELETE",
    headers,
  });

  if (res.status === 204) return;

  let detail = `Failed to delete comment (status ${res.status})`;
  try {
    const data = await res.json();
    if (data?.detail) detail = data.detail;
  } catch {}
  throw new Error(detail);
};

export const editComment = async (commentId, content) => {
  const headers = await getAuthHeader();

  const res = await fetch(`${API_BASE_URL}/api/comments/${commentId}/`, {
    method: "PUT",
    headers,
    body: JSON.stringify({ content }),
  });

  if (res.ok) return await res.json();

  let detail = `Failed to edit comment (status ${res.status})`;
  try {
    const data = await res.json();
    if (data?.detail) detail = data.detail;
  } catch {}
  throw new Error(detail);
};


export async function fetchUserBadges() {
  const headers = await getAuthHeader();

  const response = await fetch(`${API_BASE_URL}/api/user/badges/`, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    let message = `Failed to return badges (status ${response.status})`;
    let body;
    try {
      body = await response.text(); // read once
      const json = JSON.parse(body);
      message = json.detail || message;
    } catch {
      console.error("Non-JSON error response:", body);
    }
    throw new Error(message);
  }

  return await response.json();
}
 
export const platformMetrics = async () => {
  try {
    const headers = await getAuthHeader();
    const { data } = await axios.get(
      `${API_BASE_URL}/api/platform-metrics/`,
      { headers }
    );

    // tolerate either snake_case (Django) or camelCase
    const messagesSent =
      Number(data?.messages_sent ?? data?.messagesSent ?? 0);
    const verifiedAccounts =
      Number(data?.verified_accounts ?? data?.verified_constituents ?? data?.verifiedConstituents ?? data?.verifiedAccounts ?? 0);
    const verifiedConstituents = verifiedAccounts; // legacy alias kept for existing consumers
    const verifiedPosts =
      Number(data?.verified_posts ?? data?.verifiedPosts ?? 0);
    const totalUsers = Number(data?.total_users ?? data?.totalUsers ?? 0);

    return { messagesSent, verifiedAccounts, verifiedConstituents, verifiedPosts, totalUsers };
  } catch (err) {
    console.error("Error fetching platform metrics:", err.response?.data || err.message);
    return { messagesSent: 0, verifiedAccounts: 0, verifiedConstituents: 0, verifiedPosts: 0, totalUsers: 0 };
  }
};

export async function getMemberPhotoUrl(bioguideId) {
  if (!bioguideId) return undefined;
  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/member_photo/${encodeURIComponent(bioguideId)}/`,
      { method: "GET" } // public; no auth header
    );
    if (!res.ok) return undefined;
    const data = await res.json();
    return data?.image_url || undefined;
  } catch {
    return undefined;
  }
}